import {
  Box,
  Breadcrumb,
  Flex,
  Layout,
  PageTitle,
  Space,
  type Tabs,
  theme,
} from '@/components';
import { useChangeBrowserTabTitleEffect } from '@/hooks';
import {
  HEADER_ID_FOR_OFFSET_HEIGHT,
  PAGE_HEADER_PADDING,
  Z_INDEX_CONTENT_HEADER,
} from '@/utils';
import type { ComponentProps, ComponentType, FC, ReactNode } from 'react';

const DEFAULT_HEADER_HEIGHT = 80;
const HEADER_BORDER_BOTTOM_WIDTH = 1;

type Props = {
  title: string | ReactNode;
  breadcrumbs?: ComponentProps<typeof Breadcrumb>['items'] | ReactNode;
  buttons?: ReactNode;
  contents?: ReactNode;
  height?: number | 'auto';
  TabsComponent?: ComponentType<
    Pick<ComponentProps<typeof Tabs>, 'tabBarStyle'>
  >;
};

export const PageHeader: FC<Props> = ({
  title,
  breadcrumbs,
  buttons,
  contents,
  height,
  TabsComponent,
}) => {
  const { token } = theme.useToken();
  useChangeBrowserTabTitleEffect({
    title: typeof title === 'string' ? title : undefined,
  });

  return (
    <Layout.Header
      id={HEADER_ID_FOR_OFFSET_HEIGHT}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: Z_INDEX_CONTENT_HEADER, // Higher zIndex reduce overlapping issue.
        width: '100%',
        padding: 0,
        background: token.colorBgBase,
        borderBottom: `${HEADER_BORDER_BOTTOM_WIDTH}px solid ${token.colorFill}`,
        boxSizing: 'border-box',
        lineHeight: 'normal',
        height: height ?? (TabsComponent ? 'auto' : DEFAULT_HEADER_HEIGHT),
      }}
      data-dd-privacy="allow"
    >
      <Box
        paddingLeft={PAGE_HEADER_PADDING}
        paddingRight={PAGE_HEADER_PADDING}
        paddingTop={13}
        paddingBottom={TabsComponent ? 0 : 13}
        gap={8}
      >
        {breadcrumbs &&
          (Array.isArray(breadcrumbs) ? (
            <Breadcrumb items={breadcrumbs} />
          ) : (
            breadcrumbs
          ))}
        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center" gap={8}>
            <Space
              style={{ flexGrow: 1, overflow: 'hidden' }}
              direction="vertical"
              size={0}
            >
              {typeof title === 'string' ? <PageTitle text={title} /> : title}
            </Space>
            <Space>{buttons}</Space>
          </Flex>
          {contents}
          {TabsComponent && (
            <TabsComponent tabBarStyle={{ marginBottom: -1 }} />
          )}
        </Flex>
      </Box>
    </Layout.Header>
  );
};
